// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * The Power Flow model: the four pure stages assembled into one structure, plus
 * the content hash that decides when it is rebuilt.
 *
 * See docs/power-flow/power-flow-single-line-diagram.md §7.
 *
 *   powerFlowGraph  → who is on the diagram, and what the payload didn't say
 *   powerFlowRank   → electrical depth
 *   powerFlowLayout → coordinates
 *   powerFlowRouting→ conductor paths
 *
 * **The structure/live split is the load-bearing part** and it is carried over
 * from `kgModel.ts` for the same reason it existed there. Everything in
 * `PowerFlowModel` is geometry and topology; nothing in it is status or
 * measurement. So a telemetry poll re-renders identical coordinates with
 * different colours, React reconciles in place, and the viewport (a ref inside
 * `useStageTransform`) and the selection (the shell store) survive untouched.
 * Without it the diagram resets under the operator's hand every 60 seconds.
 *
 * `computeStructureKey` exists because the poll hands back fresh array
 * identities every minute even when nothing changed, so `[entities,
 * relationships]` is useless as a memo dependency.
 */

import type { Entity, Relationship } from '../types/digitalTwin';
import { isFlowRelationshipType, isFluidRelationshipType } from './flowRelationships';
import {
  buildPowerFlowGraph,
  type PowerFlowDiagnostics,
  type PowerFlowGraph,
} from './powerFlowGraph';
import { inferPowerFlow, type PowerFlowInference } from './powerFlowInference';
import { rankPowerFlow, type PowerFlowRanking } from './powerFlowRank';
import {
  collapsePowerFlow,
  type PowerFlowDisplay,
  type PowerFlowLoadGroup,
} from './powerFlowCollapse';
import {
  DEFAULT_LAYOUT_OPTIONS,
  type PowerFlowLayout,
  type PowerFlowLayoutOptions,
} from './powerFlowLayout';
import type { PowerFlowConductor } from './powerFlowRouting';
import { blockOfDrawn, buildPowerFlowSheets, type PowerFlowSheets } from './powerFlowSheets';

export interface PowerFlowModel {
  graph: PowerFlowGraph;
  /**
   * What the topology and labels imply beyond what the payload states: blocks,
   * feeder groups, source selection, bypasses, branch class. Structural, so it
   * is rebuilt with the geometry and never on a poll.
   */
  inference: PowerFlowInference;
  ranking: PowerFlowRanking;
  /** The diagram as drawn: repeated terminal loads summarised into groups. */
  display: PowerFlowDisplay;
  /** Load groups, collapsed or expanded, in a stable order. */
  groups: PowerFlowLoadGroup[];
  /**
   * The drawing as bands of block columns (`powerFlowSheets`), with the chrome
   * the stage draws around them. `layout` and `conductors` below are the same
   * objects, kept for callers that want only the geometry.
   */
  sheets: PowerFlowSheets;
  layout: PowerFlowLayout;
  conductors: PowerFlowConductor[];
  /**
   * Everything the payload could not tell us, in one place: the graph's own
   * findings plus the ranking's role and root checks.
   */
  diagnostics: PowerFlowModelDiagnostics;
  /** True when there is anything to draw. */
  hasDiagram: boolean;
}

export interface PowerFlowModelDiagnostics extends PowerFlowDiagnostics {
  /** Rank-0 assets the ontology says should not be roots. */
  suspectRoots: string[];
  /** Assets whose published edges contradict their SHACL role. */
  roleViolations: string[];
  /** Links removed to break a cycle — bus ties. Drawn, not dropped. */
  tieCount: number;
  /** Ranks in the drawing. Four is what today's payloads produce. */
  rankCount: number;
  /** Assets drawn. */
  assetCount: number;
  /** Conductors drawn. */
  conductorCount: number;
  /**
   * Terminal loads currently summarised behind a count badge. Not a problem —
   * the reader can fan any group out — but it is the difference between the
   * asset count and the number of boxes on screen, so it is reported.
   */
  groupedLoadCount: number;
  /** True when nothing needs reporting to the operator. */
  clean: boolean;
}

export interface BuildPowerFlowModelOptions {
  layout?: Omit<Partial<PowerFlowLayoutOptions>, 'orderKeyOf'>;
  /**
   * Load-group ids the reader has fanned back out into individual chips.
   *
   * Part of the structural model rather than live state: expanding a group
   * changes the geometry, so it has to reach the layout. It is also why the
   * page memoises on this set alongside the content hash.
   */
  expandedGroups?: ReadonlySet<string>;
  /** Fewest identical loads worth summarising. See `powerFlowCollapse`. */
  minGroupSize?: number;
  /**
   * Fold terminal loads into the bars that feed them and badge the buses that
   * leaves terminal (`RPP × 3`). On by default: it is what makes a 182-rack
   * site legible. See `powerFlowCollapse`.
   */
  foldTerminalLoads?: boolean;
  /**
   * Short name for an asset in an off-sheet stub (`SGO-A`, `UPS-A1`). Defaults
   * to the layout's `labelOf`, which on a real site is the long published
   * label; the page passes the twin's `upstreamId` where one exists.
   */
  stubLabelOf?: (_id: string) => string;
  /**
   * Which tab of blocks to draw — see `SheetPage`. Omitted, or on a plant with
   * one page's worth of blocks, draws everything. Only the *drawing* is paged:
   * `graph`, `inference` and the KPIs stay whole-site, so the tab strip can
   * list every block and a reading never depends on which tab is open.
   */
  sheetPage?: number;
}

/**
 * Build the structural model.
 *
 * Deterministic: identical content produces an identical model regardless of
 * the order the two arrays arrived in. Asserted by shuffling them, because the
 * details endpoint is cursor-paginated and page order is not stable.
 */
export const buildPowerFlowModel = (
  entities: readonly Entity[],
  relationships: readonly Relationship[],
  options: BuildPowerFlowModelOptions = {}
): PowerFlowModel => {
  const graph = buildPowerFlowGraph(entities, relationships);
  const inference = inferPowerFlow(graph, entities);
  const ranking = rankPowerFlow(graph);
  const display = collapsePowerFlow(graph, ranking, {
    expanded: options.expandedGroups,
    minGroupSize: options.minGroupSize,
    foldTerminalLoads: options.foldTerminalLoads ?? true,
    // With blocks, the sheet layout stacks RPPs under their PDU; without, the
    // `RPP × n` badge is what keeps the single drawing narrow.
    groupTerminalBuses: (options.foldTerminalLoads ?? true) && inference.blocks.length === 0,
  });
  /**
   * The grouping key the layout keeps contiguous: feeder group, then block.
   *
   * This is not a cosmetic bias. Every bus is drawn as a bar spanning the taps
   * beneath it, so a rank whose blocks are interleaved produces a bar that
   * spans another block's assets — a drawing that asserts two blocks share a
   * potential. `powerFlowLayout` therefore sorts on this before any barycenter
   * round, and a stable key is what makes the bar safe to generalise.
   *
   * A collapsed load group inherits its key from the assets that feed it. Its
   * own id is synthetic and in no block, and without this a rank's rack groups
   * would all sort to the far right, away from the RPPs they hang off. A group
   * whose feeds span two blocks is in neither and takes the neutral key, the
   * same as an asset in no block — see `blockOfDrawn`.
   */
  const orderKeyOf = (id: string): string => {
    const blockId = blockOfDrawn(display, inference, id);
    if (!blockId) return '~';
    return `${inference.feederGroupOfBlock.get(blockId) ?? '~'}|${blockId}`;
  };
  // One column per block and two sheets when the plant has blocks and an
  // output board; otherwise one sheet, one implicit column, no chrome. Routing
  // follows the **display's** links, not the ranking's: a collapsed group has
  // one conductor per feed, not one per rack behind it.
  const sheets = buildPowerFlowSheets(graph, ranking, display, inference, {
    // `orderKeyOf` last, and omitted from the option type above: it carries the
    // feeder/block contiguity invariant this module is responsible for, and a
    // caller that replaced it could make a generalised bus bar span another
    // block's taps. Everything else in `layout` is the caller's to set.
    // A site that publishes breakers draws them in series on the drops, at the
    // board they belong to, with padding to the bar; the default rank gap has
    // no room for that. The caller can still set its own gap.
    layout: {
      ...(graph.links.some(link => (link.via?.length ?? 0) > 0)
        ? {
            rankGapY:
              (options.layout?.nodeHeight ?? DEFAULT_LAYOUT_OPTIONS.nodeHeight) + BREAKER_RANK_GAP,
          }
        : {}),
      ...options.layout,
      orderKeyOf,
    },
    labelOf: options.stubLabelOf ?? options.layout?.labelOf,
    pageIndex: options.sheetPage,
  });
  const { layout, conductors } = sheets.sheet;

  const diagnostics: PowerFlowModelDiagnostics = {
    ...graph.diagnostics,
    suspectRoots: ranking.suspectRoots,
    roleViolations: ranking.roleViolations,
    tieCount: ranking.cycleLinks.length,
    rankCount: ranking.ranks.length,
    assetCount: graph.vertices.length,
    conductorCount: conductors.length,
    groupedLoadCount: display.collapsedIds.size,
    clean: false,
  };
  diagnostics.clean = isClean(diagnostics);

  return {
    graph,
    inference,
    ranking,
    display,
    groups: display.groups,
    sheets,
    layout,
    conductors,
    diagnostics,
    hasDiagram: layout.nodes.length > 0,
  };
};

/**
 * Clear gap between ranks on a site with breakers, stage px — measured from a
 * node's underside, so it holds whatever node height the caller lays out at
 * (the page uses 62, the layout default is 44).
 *
 * A breaker needs a bus text band (20), the padding to its board (12), its own
 * 46 px and clearance at the far end (8) — 86, asserted against
 * `BREAKER_RUN_NEEDED` in the breaker tests. 104 also leaves visible run
 * between the breaker and the device it feeds.
 */
export const BREAKER_RANK_GAP = 104;

/**
 * Whether there is anything to tell the operator.
 *
 * The test is "is something absent from the drawing", not "is something
 * broken". A tie is deliberately *not* listed: it is a real feature of a
 * redundant plant and the drawing shows it, so there is nothing to explain. An
 * excluded air header is listed even though excluding it is correct, because
 * it is *not* drawn and a reader looking for it would otherwise have to guess.
 * Everything else in the list is something the payload failed to express.
 */
const isClean = (diagnostics: PowerFlowModelDiagnostics): boolean =>
  diagnostics.fluidPathCount === 0 &&
  diagnostics.unconnectedCount === 0 &&
  diagnostics.danglingLinkCount === 0 &&
  diagnostics.uncontainedTerminalLinkCount === 0 &&
  diagnostics.unmappedTypes.length === 0 &&
  diagnostics.unknownRelationshipTypes.length === 0 &&
  diagnostics.attributeTopologyCount === 0 &&
  diagnostics.suspectRoots.length === 0 &&
  diagnostics.roleViolations.length === 0;

/**
 * A hash of everything the layout depends on: which entities exist, their
 * types, the naming and ratings the structural passes read, and which flow
 * relationships connect them.
 *
 * Labels and `upstreamId` are *in* the hash, not out of it: block inference
 * reads the block letter off them (`Transformer-Block-A`, `SWGR-1-A`), the
 * off-sheet stubs print `upstreamId`, and a rename can therefore move an asset
 * from one column to another. `ratedVoltage` decides an asset's voltage class
 * (conductor weight) and `ratedPower` is a nameplate the structure carries.
 * Status and measurements stay excluded — a new reading recolours a chip, it
 * never moves one.
 *
 * Order-independent by construction (count + additive sum + xor), so the same
 * content hashes the same however the API paginated it. That avoids sorting
 * thousands of strings on every poll.
 */
export const computePowerFlowStructureKey = (
  entities: readonly Entity[],
  relationships: readonly Relationship[]
): string => {
  let entityCount = 0;
  let entitySum = 0;
  let entityXor = 0;
  for (const entity of entities) {
    const attributes = entity.attributes ?? {};
    const hash = fnv1a(
      [
        entity.id,
        entity.type,
        structuralAttribute(attributes.label),
        structuralAttribute(attributes.upstreamId),
        structuralAttribute(attributes.ratedVoltage),
        structuralAttribute(attributes.ratedPower),
      ].join(FIELD_SEP)
    );
    entityCount += 1;
    entitySum = (entitySum + hash) >>> 0;
    entityXor = (entityXor ^ hash) >>> 0;
  }

  let relCount = 0;
  let relSum = 0;
  let relXor = 0;
  for (const rel of relationships) {
    // Non-flow relationships cannot change the layout, but telemetry links can
    // change which entities are filtered off the diagram, and fluid paths feed
    // the Floor Plan notice, so all three kinds count.
    if (
      !isFlowRelationshipType(rel.type) &&
      !isFluidRelationshipType(rel.type) &&
      !isStructuralRelationshipType(rel.type)
    ) {
      continue;
    }
    const hash = fnv1a(`${rel.source}${FIELD_SEP}${rel.target}${FIELD_SEP}${rel.type}`);
    relCount += 1;
    relSum = (relSum + hash) >>> 0;
    relXor = (relXor ^ hash) >>> 0;
  }

  return [
    entityCount,
    entitySum.toString(36),
    entityXor.toString(36),
    relCount,
    relSum.toString(36),
    relXor.toString(36),
  ].join(':');
};

/**
 * Relationship types that are not flow but still affect the diagram: telemetry
 * links determine which entities are filtered out as telemetry points, and
 * containment decides which board a breaker folds into.
 */
const STRUCTURAL_RELATIONSHIP_TYPES = new Set([
  'hasTelemetryPoint',
  'hasTelemetryStream',
  'hasPoint',
  'isTelemetryOf',
  'isTelemetryStreamOf',
  'hasParameter',
  'isParameterOf',
  'hasPart',
  'isPartOf',
]);

const isStructuralRelationshipType = (type: string): boolean =>
  STRUCTURAL_RELATIONSHIP_TYPES.has(type);

/**
 * A structural attribute as hashed: stringified, with a missing one as the
 * empty string so `{ label: undefined }` and no label hash alike. Ratings come
 * in three encodings (`{ value, unit }`, `'2000 A'`, a bare number) and a
 * structured one is serialised rather than read as `[object Object]`.
 */
const structuralAttribute = (value: unknown): string => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

/**
 * ASCII unit separator, used to delimit fields before hashing so that
 * ("ab", "c") and ("a", "bc") cannot hash to the same value.
 */
const FIELD_SEP = '\x1f';

/** FNV-1a, 32-bit. Small, fast, and good enough to detect content changes. */
const fnv1a = (value: string): number => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
};
